export type ProgressConfig = { enabled: boolean; excluded_tests?: string[]; memory_trainer_tests?: Record<string, string> };
export type SavedRun = {
  result_id: string; test: string; at: string; locale: string; device: string;
  config_hash: string; scorer_version: string; flow: string | null; source: string;
  primary: { metric: string; value: number; unit: string; higher_is_better: boolean } | null;
  metrics: Record<string, number>;
  settings: Record<string, string | number>;
};
export type AccountHistory = {
  entries: SavedRun[]; total: number; next_offset: number | null;
  account: { email: string; user_guid: string };
};
export class ProgressError extends Error {
  constructor(public status: number) { super(`Progress request failed (${status})`); }
}
const ID_KEY = "helibrain.saved-result-ids.v1";
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let configRequest: Promise<ProgressConfig> | undefined;
export function progressConfig(): Promise<ProgressConfig> {
  return configRequest ??= fetch("/free_tests/progress", { cache: "no-store" })
    .then(async r => { if (!r.ok) throw new ProgressError(r.status); return await r.json(); })
    .catch(error => { configRequest = undefined; throw error; });
}
export function historyUrl(locale = "en", save = false) {
  return `${locale === "en" ? "" : `/${locale}`}/tools/history${save ? "?save=1" : ""}`;
}
export function rememberResult(value: unknown): boolean {
  if (typeof value !== "string" || !uuid.test(value)) return false;
  try {
    const old = JSON.parse(localStorage.getItem(ID_KEY) || "[]");
    const ids = Array.isArray(old) ? old.filter(x => typeof x === "string" && uuid.test(x)) : [];
    localStorage.setItem(ID_KEY, JSON.stringify([...new Set([...ids, value])].slice(-1000)));
    return true;
  } catch { return false; } // The cookie still proves server rows when storage is unavailable.
}
export function localResultIds(): string[] {
  const ids = new Set<string>();
  const visit = (v: unknown, depth = 0) => {
    if (!v || typeof v !== "object" || depth > 6) return;
    if (Array.isArray(v)) { v.slice(-1000).forEach(x => visit(x, depth + 1)); return; }
    const r = v as Record<string, unknown>;
    if (typeof r.result_id === "string" && uuid.test(r.result_id)) ids.add(r.result_id);
    for (const key of ["entries", "runs", "score"]) visit(r[key], depth + 1);
  };
  try {
    const stored = JSON.parse(localStorage.getItem(ID_KEY) || "[]");
    if (Array.isArray(stored)) stored.forEach(x => { if (typeof x === "string" && uuid.test(x)) ids.add(x); });
    for (const key of ["helibrain.progress.v1", "helibrain.trainers.v1", "hb_tools_aim_practice_v1", "hb_tools_typing_practice_v1", "hb_tools_reading_practice_v1"]) {
      const raw = localStorage.getItem(key);
      if (raw && raw.length <= 2_000_000) {
        try { visit(JSON.parse(raw)); } catch { /* Ignore one corrupt local backup; other keys remain importable. */ }
      }
    }
  } catch { /* Browser storage is optional; claim-cookie possession is checked on the server. */ }
  return [...ids].slice(-1000);
}
async function request<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(path, {
    method: body === undefined ? "GET" : "POST", credentials: "same-origin", cache: "no-store",
    headers: { "Content-Type": "application/json", "X-Helibrain-CSRF": "1" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) throw new ProgressError(response.status);
  return response.json();
}
export const readAccountHistory = (offset = 0) => request<AccountHistory>(`/free_tests/history?offset=${offset}`);
export const claimProgress = () => request<{ total: number; skipped_local: number }>("/free_tests/claim", { result_ids: localResultIds() });
export async function saveMemoryPractice(kind: string, payload: Record<string, unknown>): Promise<string | null> {
  const config = await progressConfig();
  if (!config.enabled) return null;
  const test = config.memory_trainer_tests?.[kind];
  if (!test) throw new Error("Missing trainer history configuration");
  return savePractice(test, payload);
}
export async function savePractice(test: string, payload: Record<string, unknown>): Promise<string | null> {
  if (!(await progressConfig()).enabled) return null;
  const result = await request<{ result_id: string }>(`/free_tests/${test}/score`, payload);
  rememberResult(result.result_id);
  return result.result_id;
}
